import { lazy, Suspense, useEffect, useLayoutEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { ToastProvider } from '../shared/components/Toast';
import { useSettings } from '../shared/hooks/useData';
import { AppShell } from './AppShell';
import { PwaStatus } from '../pwa/PwaStatus';

const WorkoutsPage = lazy(() => import('../features/workouts/WorkoutsPage'));
const ActiveWorkoutPage = lazy(() => import('../features/active-workout/ActiveWorkoutPage'));
const WorkoutDetailPage = lazy(() => import('../features/workouts/WorkoutDetailPage'));
const PlansPage = lazy(() => import('../features/plans/PlansPage'));
const PlanEditorPage = lazy(() => import('../features/plans/PlanEditorPage'));
const ProgressPage = lazy(() => import('../features/progress/ProgressPage'));
const SettingsPage = lazy(() => import('../features/settings/SettingsPage'));
const ExercisesPage = lazy(() => import('../features/exercises/ExercisesPage'));
const ProfilePage = lazy(() => import('../features/coaching/ProfilePage'));
const ProfileEditPage = lazy(() => import('../features/coaching/ProfileEditPage'));

function ThemeController() {
  const settings = useSettings();
  useEffect(() => {
    const theme = settings?.theme ?? 'system';
    document.documentElement.dataset.theme = theme;
    document.documentElement.style.colorScheme = theme === 'system' ? 'light dark' : theme;
  }, [settings?.theme]);
  return null;
}

function RouteScrollReset() {
  const { pathname } = useLocation();
  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    document.querySelector<HTMLElement>('.app-content')?.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname]);
  return null;
}

export function App() {
  return (
    <ToastProvider>
      <BrowserRouter>
        <ThemeController />
        <RouteScrollReset />
        <PwaStatus />
        <a className="skip-link" href="#main-content">Перейти к содержимому</a>
        <Suspense fallback={<main className="bootstrap-screen" aria-busy="true"><div className="spinner" /><p>Загружаем экран…</p></main>}>
          <Routes>
            <Route element={<AppShell />}>
              <Route index element={<Navigate replace to="/workouts" />} />
              <Route path="/workouts" element={<WorkoutsPage />} />
              <Route path="/plans" element={<PlansPage />} />
              <Route path="/progress" element={<ProgressPage />} />
            </Route>
            <Route path="/workouts/active" element={<ActiveWorkoutPage />} />
            <Route path="/workouts/:id" element={<WorkoutDetailPage />} />
            <Route path="/plans/new" element={<PlanEditorPage />} />
            <Route path="/plans/:id/edit" element={<PlanEditorPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/profile/edit" element={<ProfileEditPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/exercises" element={<ExercisesPage />} />
            <Route path="*" element={<Navigate replace to="/workouts" />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </ToastProvider>
  );
}
